import { Divider, Skeleton } from "antd"
import { useState } from "react"
import { useGet } from "utils/useFetch"
import { IShiftSwapRequest, IShiftSwapRequestRes } from "../interface/shiftData"
import { SwapRequestDetails } from "./SwapRequestDetails"
import ShiftSwapRequestModal from "./ShiftSwapRequestModal"
import { ShiftSwapCollapse } from "./ShiftSwapCollapse"
import { ShiftSwapFilters } from "./ShiftSwapFilters"

type SwapWithPerspective = IShiftSwapRequest & {
  perspective: "sender" | "receiver"
}

interface ShiftSwapProps {
  planAllowsSwapping: boolean
  isLoading?: boolean
}

export const ShiftSwap = ({
  planAllowsSwapping,
  isLoading,
}: ShiftSwapProps) => {
  // ─── State ───────────────────────────────────────────────────────────────

  const [showRequestDetailsModal, setShowRequestDetailsModal] = useState(false)
  const [showRecievedSwapModal, setShowRecievedSwapModal] = useState(false)
  const [selectedSwap, setSelectedSwap] = useState<SwapWithPerspective | null>(
    null
  )
  const [activeTab, setActiveTab] = useState("pending")
  const [pendingFilter, setPendingFilter] = useState("all")
  const [historyFilter, setHistoryFilter] = useState("all")
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(ALL_SECTION_KEYS)
  )

  // ─── Data fetching ────────────────────────────────────────────────────────

  const { data } = useGet<IShiftSwapRequestRes>({
    url: "/v1/time-attendance/shift-swaps/requests/requested-by-employee",
    instanceKey: "ewaAxiosInstance",
  })

  const { data: recievedData } = useGet<IShiftSwapRequestRes>({
    url: "/v1/time-attendance/shift-swaps/requests",
    instanceKey: "ewaAxiosInstance",
  })

  // ─── Derived state ────────────────────────────────────────────────────────

  const swaps = data?.data?.docs ?? []
  const swapsRecieved = recievedData?.data?.docs ?? []

  const allSwaps = [
    ...swaps.map((s) => ({ ...s, perspective: "sender" as const })),
    ...swapsRecieved.map((s) => ({ ...s, perspective: "receiver" as const })),
  ]

  const pendingSwaps = allSwaps.filter((s) => s.status === "pending")
  const historySwaps = allSwaps.filter(
    (s) => s.status === "approved" || s.status === "declined"
  )

  const pendingSections = [
    {
      key: "awaiting-you",
      label: "Awaiting you",
      swaps: pendingSwaps.filter(
        (s) => s.perspective === "receiver" && !s.targetEmployeeApproved
      ),
    },
    {
      key: "awaiting-peer",
      label: "Awaiting peer",
      swaps: pendingSwaps.filter(
        (s) => s.perspective === "sender" && !s.targetEmployeeApproved
      ),
    },
    {
      key: "awaiting-admin",
      label: "Awaiting admin",
      swaps: pendingSwaps.filter((s) => s.targetEmployeeApproved),
    },
  ]

  const historySections = [
    {
      key: "approved",
      label: "Approved",
      swaps: historySwaps.filter((s) => s.status === "approved"),
    },
    {
      key: "rejected",
      label: "Rejected",
      swaps: historySwaps.filter((s) => s.status === "declined"),
    },
  ]

  const visiblePendingSections =
    pendingFilter === "all"
      ? pendingSections
      : pendingSections.filter((s) => s.key === pendingFilter)

  const visibleHistorySections =
    historyFilter === "all"
      ? historySections
      : historySections.filter((s) => s.key === historyFilter)

  const toggleSection = (key: string) =>
    setOpenSections((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const handleFilterChange = (key: string, setFilter: (k: string) => void) => {
    setFilter(key)
    if (key !== "all") setOpenSections((prev) => new Set([...prev, key]))
  }

  const activeFilter = activeTab === "pending" ? pendingFilter : historyFilter
  const activeFilters =
    activeTab === "pending" ? pendingFilters : historyFilters
  const visibleSections =
    activeTab === "pending" ? visiblePendingSections : visibleHistorySections

  const handleActiveFilterChange = (key: string) =>
    handleFilterChange(
      key,
      activeTab === "pending" ? setPendingFilter : setHistoryFilter
    )

  const dataLoaded = data !== undefined && recievedData !== undefined
  if (dataLoaded && !planAllowsSwapping && allSwaps.length === 0) return null

  if (isLoading) {
    return (
      <div className="xl:w-[360px] xl:shrink-0 xl:my-2 xl:mt-1">
        <div className="bg-white w-full my-2 mt-1 xl:my-0 p-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="border border-[#E2E8F0] rounded-md p-2 mt-2"
            >
              <Skeleton
                active
                title
                paragraph={{ rows: 2 }}
              />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="xl:relative xl:w-[360px] xl:shrink-0 xl:self-stretch xl:my-2 xl:mt-1">
        <div className="xl:absolute xl:inset-0 xl:overflow-hidden">
          <div className="flex flex-col bg-white w-full xl:w-[360px] md:shrink-0 my-2 mt-1 xl:h-full xl:my-0">
            <div className="flex items-center gap-2 px-3 pt-3">
              <h5 className="text-base text-padeBlack font-semibold shrink-0">
                Shift swaps
              </h5>
            </div>
            <Divider style={{ margin: "10px 0" }} />
            <div className="flex items-center mb-2 text-center w-full">
              {[
                {
                  key: "pending",
                  label: "Pending",
                  count: pendingSwaps.length,
                },
                { key: "history", label: "History" },
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center justify-center gap-2 py-2 px-3 flex-1 ${
                    activeTab === key
                      ? "border-b-2 border-[#0F172B] text-[#0F172B] font-semibold"
                      : "text-gray-500 border-b border-[#d7dfe9]"
                  }`}
                >
                  <span>{label}</span>
                  {count !== undefined && (
                    <span className="flex items-center justify-center w-5 h-5 px-1.5 rounded-full bg-[#F1F5F9] text-[#0F172B] font-semibold leading-none">
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <ShiftSwapFilters
              filters={activeFilters}
              activeFilter={activeFilter}
              onChange={handleActiveFilterChange}
            />
            <div
              className="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-2"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#90A1B9 transparent",
              }}
            >
              {visibleSections.map((section) => (
                <ShiftSwapCollapse
                  key={section.key}
                  section={section}
                  isOpen={openSections.has(section.key)}
                  onToggle={() => toggleSection(section.key)}
                  onViewRequest={(swap) => {
                    setSelectedSwap(swap)
                    const receiverMustAct =
                      swap.perspective === "receiver" &&
                      swap.status === "pending" &&
                      !swap.targetEmployeeApproved
                    receiverMustAct
                      ? setShowRecievedSwapModal(true)
                      : setShowRequestDetailsModal(true)
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <SwapRequestDetails
        open={showRequestDetailsModal}
        onClose={() => setShowRequestDetailsModal(false)}
        swap={selectedSwap}
        perspective={selectedSwap?.perspective ?? "sender"}
      />
      <ShiftSwapRequestModal
        open={showRecievedSwapModal}
        onClose={() => setShowRecievedSwapModal(false)}
        swap={selectedSwap}
      />
    </>
  )
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_SECTION_KEYS = [
  "awaiting-you",
  "awaiting-peer",
  "awaiting-admin",
  "approved",
  "rejected",
]

const pendingFilters = [
  { key: "all", label: "All" },
  { key: "awaiting-you", label: "Awaiting you" },
  { key: "awaiting-peer", label: "Awaiting peer" },
  { key: "awaiting-admin", label: "Awaiting admin" },
]

const historyFilters = [
  { key: "all", label: "All" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
]
